import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Cms, RentalCarClass as DracarCarClass } from '@impact/data';
import { IMPACT_OVERLAY_DATA, ImpactOverlayRef } from '@impactdk/ngx-overlay';
import { TranslateService } from '@ngx-translate/core';
import { Subject, throwError } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { closeLightIcon } from '../../../icons';
import { BreakpointService } from '../../breakpoint/breakpoint.service';
import { BookWorkshopFlowService } from '../../core/book-workshop-flow.service';
import { ToastService } from '../../core/toast.service';
import { WorkshopService } from '../../core/workshop.service';
import { FormService } from '../../form/form.service';
import { BookWorkshopStep, IBooking } from '../../models/book-workshop.models';
import { IStep } from '../../stepper/stepper.component';

interface OverlayData {
    pageContent: Cms.BookWorkshopPage;
}

export interface RentalCarClass extends DracarCarClass {
    displayName: string | undefined;
    image: Cms.Media | undefined;
    price: string | undefined;
}

const trackingRef = 'book-workshop-flow';

@Component({
    selector: 'app-book-workshop-stepper',
    template: `
        <section>
            <header class="full-page-takeover-header book-workshop-stepper-header">
                <div class="full-page-takeover-header-titles">
                    <div class="full-page-takeover-header-titles__title">
                        {{ 'book_workshop.book_workshop' | translate }}
                    </div>
                </div>

                <ng-container *ngIf="isLargeScreen$ | async">
                    <app-stepper
                        *ngIf="step !== BookWorkshopStep.Success"
                        class="book-workshop-stepper-header__stepper full-page-takeover-header__progress-bar"
                        [steps]="steps"
                        [index]="step"
                    >
                    </app-stepper>
                </ng-container>

                <div class="full-page-takeover-header-close">
                    <app-button
                        buttonClass="full-page-takeover-header-close-button button--round-with-icon"
                        [title]="'buttons.close' | translate"
                        (click)="onClose()">
                        <i class="button__icon">${closeLightIcon}</i>
                    </app-button>
                </div>
            </header>

            <div [ngSwitch]="step" class="book-workshop-stepper-content">
                <ng-container *ngIf="!(isLargeScreen$ | async)">
                    <app-stepper
                        *ngIf="step !== BookWorkshopStep.Success"
                        class="book-workshop-stepper-header__stepper full-page-takeover-header__progress-bar"
                        [steps]="steps"
                        [index]="step"
                    >
                    </app-stepper>
                </ng-container>

                <app-book-workshop-car-info
                    *ngSwitchCase="BookWorkshopStep.CarInfo"
                    [booking]="booking"
                    (regNumberChange)="onRegNumberChange($event)"
                    (nextStep)="nextStep($event)"
                ></app-book-workshop-car-info>
                <app-book-workshop-user-info
                    *ngSwitchCase="BookWorkshopStep.UserInfo"
                    [booking]="booking"
                    (previousStep)="prevStep($event)"
                    (nextStep)="nextStep($event)"
                ></app-book-workshop-user-info>
                <app-book-workshop-select-shop
                    *ngSwitchCase="BookWorkshopStep.SelectShop"
                    [booking]="booking"
                    [pageContent]="pageContent"
                    (previousStep)="prevStep($event)"
                    (nextStep)="nextStep($event)"
                ></app-book-workshop-select-shop>
                <app-book-workshop-select-service
                    *ngSwitchCase="BookWorkshopStep.SelectService"
                    [booking]="booking"
                    (previousStep)="prevStep($event)"
                    (nextStep)="nextStep($event)"
                ></app-book-workshop-select-service>
                <app-book-workshop-select-delivery
                    *ngSwitchCase="BookWorkshopStep.SelectDelivery"
                    [booking]="booking"
                    (previousStep)="prevStep($event)"
                    (nextStep)="nextStep($event)"
                ></app-book-workshop-select-delivery>
                <app-book-workshop-select-date
                    *ngSwitchCase="BookWorkshopStep.SelectDate"
                    [booking]="booking"
                    [pageContent]="pageContent"
                    [rentals]="rentalCars"
                    (previousStep)="prevStep($event)"
                    (nextStep)="nextStep($event)"
                ></app-book-workshop-select-date>
                <app-book-workshop-summary
                    *ngSwitchCase="BookWorkshopStep.Summary"
                    [booking]="booking"
                    [pageContent]="pageContent"
                    [rentals]="rentalCars"
                    [loading]="loading"
                    (previousStep)="prevStep($event)"
                    (nextStep)="nextStep($event)"
                ></app-book-workshop-summary>
                <app-book-workshop-success
                    *ngSwitchCase="BookWorkshopStep.Success"
                    [clientEmail]="clientEmail"
                    [richText]="pageContent.bookingSuccess"
                    (closed)="onClose()"
                ></app-book-workshop-success>
            </div>
        </section>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookWorkshopStepperComponent implements OnInit, OnDestroy {
    private unsubscribe = new Subject<void>();

    booking: IBooking;
    BookWorkshopStep = BookWorkshopStep;
    clientEmail?: string;
    isLargeScreen$ = this.breakpointService.matchBreakpoint(1024);
    loading = false;
    pageContent = this.data.pageContent;
    rentalCars: RentalCarClass[] = [];
    step: BookWorkshopStep;
    steps: IStep[];

    constructor(
        private bookWorkshopFlowService: BookWorkshopFlowService,
        private breakpointService: BreakpointService,
        private cdr: ChangeDetectorRef,
        private formService: FormService,
        private overlayRef: ImpactOverlayRef,
        private toastService: ToastService,
        private translateService: TranslateService,
        private workshopService: WorkshopService,
        @Inject(IMPACT_OVERLAY_DATA) private data: OverlayData,
    ) {
        translateService
            .get([
                'book_workshop.step_car_info',
                'book_workshop.step_workshop',
                'book_workshop.step_service',
                'book_workshop.step_delivery',
                'book_workshop.step_date',
                'book_workshop.step_info',
                'book_workshop.step_summary',
            ])
            .pipe(take(1))
            .subscribe((translations: { [key: string]: string }) => {
                this.steps = [
                    { title: translations['book_workshop.step_car_info'] },
                    { title: translations['book_workshop.step_workshop'] },
                    { title: translations['book_workshop.step_service'] },
                    { title: translations['book_workshop.step_delivery'] },
                    { title: translations['book_workshop.step_date'] },
                    { title: translations['book_workshop.step_info'] },
                    { title: translations['book_workshop.step_summary'] },
                ];
            });

        this.bookWorkshopFlowService.booking$.pipe(takeUntil(this.unsubscribe)).subscribe((booking) => {
            this.booking = booking;
            cdr.markForCheck();
        });

        this.bookWorkshopFlowService.initBooking();

        this.bookWorkshopFlowService.step$.pipe(takeUntil(this.unsubscribe)).subscribe((step) => {
            this.step = step;
            cdr.markForCheck();

            if (step > 0 && step !== BookWorkshopStep.Success) {
                this.formService.trackStep(trackingRef, 1 + step);
            }
        });
    }

    ngOnInit(): void {
        this.formService.trackInitialization(trackingRef);
        this.bookWorkshopFlowService.deliveryOptionInfoTexts = this.pageContent.deliveryOptionInfoTexts ?? [];
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    onRegNumberChange(regNumber: string) {
        this.steps[0].subtitle = regNumber;
        this.steps = [...this.steps];
    }

    nextStep(stepData: Partial<IBooking>) {
        if (this.step === BookWorkshopStep.Summary) {
            this.loading = true;
            this.bookWorkshopFlowService.saveBooking(this.booking);

            this.workshopService
                .createBooking(this.booking)
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        this.loading = false;
                        this.toastService.show(this.translateService.instant('book_workshop.error_message____generic'));
                        this.cdr.markForCheck();
                        return throwError(error);
                    }),
                    takeUntil(this.unsubscribe)
                )
                .subscribe(() => {
                    // Preserve email for success step
                    this.clientEmail = this.booking.email;
                    this.bookWorkshopFlowService.nextStep(stepData);

                    this.formService.trackCompletion(trackingRef, this.booking.consent, {
                        make: this.booking.carIndicataData?.make?.summary,
                        model: this.booking.carIndicataData?.model?.summary,
                        dealershipZipcode: this.booking.shop?.zipCode,
                    });

                    this.bookWorkshopFlowService.resetBooking();
                });
        } else {
            this.bookWorkshopFlowService.nextStep(stepData);

            if (this.step === BookWorkshopStep.SelectService) {
                this.resolveRentalCars();
            }
        }
    }

    prevStep(stepData?: Partial<IBooking>) {
        this.bookWorkshopFlowService.prevStep(stepData);
    }

    onClose() {
        this.overlayRef.close();
        this.bookWorkshopFlowService.resetStep();
    }

    private resolveRentalCars() {
        this.workshopService
            .getAvailableRentalCarClasses(this.booking)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((carClasses) => {
                this.rentalCars = carClasses.map((carClass) => {
                    const umbracoItem =
                        this.data.pageContent.rentalCarImages.find(
                            (r) => r.rentalCarId === carClass.carClassId?.toString()
                        );

                    return {
                        ...carClass,
                        image: umbracoItem?.rentalCarImage,
                        displayName:
                            umbracoItem?.rentalCarName ??
                            carClass.name ??
                            undefined,
                        price: umbracoItem?.rentingPrice,
                    };
                });

                this.cdr.markForCheck();
            });
    }
}
